import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { keyframes, alpha } from "@mui/system";

/** Instagram Stories–style conic gradient ring. */
const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const shimmerSweep = keyframes`
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
`;

const pulseSoft = keyframes`
  0%, 100% { opacity: 0.55; }
  50% { opacity: 1; }
`;

const IG_GRADIENT =
  "conic-gradient(from 180deg at 50% 50%, #f09433 0deg, #e6683c 60deg, #dc2743 120deg, #cc2366 180deg, #bc1888 240deg, #833ab4 300deg, #f09433 360deg)";

/**
 * Compact Instagram-like gradient spinner.
 * Use alone, or inside StatsLoadingScreen / IgShimmerBlock.
 */
export function IgSpinner({ size = 36, thickness = 3, sx }) {
  const hole = Math.max(size - thickness * 2, 8);
  return (
    <Box
      role="progressbar"
      aria-label="Loading"
      sx={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: IG_GRADIENT,
        animation: `${spin} 0.85s linear infinite`,
        display: "grid",
        placeItems: "center",
        flexShrink: 0,
        ...sx,
      }}
    >
      <Box
        sx={{
          width: hole,
          height: hole,
          borderRadius: "50%",
          bgcolor: "background.paper",
        }}
      />
    </Box>
  );
}

IgSpinner.propTypes = {
  size: PropTypes.number,
  thickness: PropTypes.number,
  sx: PropTypes.object,
};

/** Soft pulsing Instagram-style shimmer bar / card. */
export function IgShimmer({ height = 48, width = "100%", borderRadius = 2, sx }) {
  return (
    <Box
      aria-hidden
      sx={{
        height,
        width,
        borderRadius,
        background: (theme) => {
          const base =
            theme.palette.mode === "dark"
              ? alpha("#fff", 0.06)
              : alpha("#0f172a", 0.06);
          const shine =
            theme.palette.mode === "dark"
              ? alpha("#fff", 0.14)
              : alpha("#fff", 0.9);
          return `linear-gradient(90deg, ${base} 0%, ${shine} 45%, ${base} 90%)`;
        },
        backgroundSize: "200% 100%",
        animation: `${shimmerSweep} 1.35s ease-in-out infinite, ${pulseSoft} 1.8s ease-in-out infinite`,
        ...sx,
      }}
    />
  );
}

IgShimmer.propTypes = {
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  borderRadius: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  sx: PropTypes.object,
};

/** Centered spinner with optional label — good for sections. */
export function IgLoadingBlock({ height = 180, label = "Loading…", sx }) {
  return (
    <Box
      sx={{
        minHeight: height,
        height,
        width: "100%",
        borderRadius: 2.5,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 1.5,
        bgcolor: (theme) =>
          theme.palette.mode === "dark" ? alpha("#fff", 0.03) : alpha("#0f172a", 0.03),
        border: (theme) =>
          `1px solid ${
            theme.palette.mode === "dark" ? alpha("#fff", 0.06) : alpha("#0f172a", 0.06)
          }`,
        ...sx,
      }}
    >
      <IgSpinner size={42} thickness={3.25} />
      {label ? (
        <Box
          component="span"
          sx={{
            fontSize: 12.5,
            fontWeight: 600,
            letterSpacing: 0.2,
            color: "text.secondary",
          }}
        >
          {label}
        </Box>
      ) : null}
    </Box>
  );
}

IgLoadingBlock.propTypes = {
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  label: PropTypes.string,
  sx: PropTypes.object,
};

/** Full stats-page skeleton: cards + charts, with IG spinner. */
export function StatsLoadingSkeleton({ showCharts = true }) {
  return (
    <Box sx={{ width: "100%", pt: 1 }}>
      <Stack alignItems="center" spacing={1.25} sx={{ mb: 3, mt: 1 }}>
        <IgSpinner size={48} thickness={3.5} />
        <Box
          component="span"
          sx={{ fontSize: 13, fontWeight: 650, color: "text.secondary" }}
        >
          Loading stats…
        </Box>
      </Stack>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 220px), 1fr))",
          gap: 2,
          mb: 3,
        }}
      >
        {[0, 1, 2, 3].map((i) => (
          <IgShimmer key={i} height={112} borderRadius={2.5} />
        ))}
      </Box>

      {showCharts ? (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: 2,
          }}
        >
          <IgShimmer height={260} borderRadius={2.5} />
          <IgShimmer height={260} borderRadius={2.5} />
          <IgShimmer height={260} borderRadius={2.5} sx={{ gridColumn: { md: "1 / -1" } }} />
        </Box>
      ) : null}
    </Box>
  );
}

StatsLoadingSkeleton.propTypes = {
  showCharts: PropTypes.bool,
};

/** List/table placeholder rows with spinner header. */
export function IgListSkeleton({ rows = 6, rowHeight = 44 }) {
  return (
    <Stack spacing={1.1}>
      <Stack direction="row" justifyContent="center" sx={{ py: 1.5 }}>
        <IgSpinner size={32} />
      </Stack>
      {Array.from({ length: rows }).map((_, i) => (
        <IgShimmer key={i} height={rowHeight} borderRadius={1.5} />
      ))}
    </Stack>
  );
}

IgListSkeleton.propTypes = {
  rows: PropTypes.number,
  rowHeight: PropTypes.number,
};

export default IgSpinner;
